/**
 * Keeping every tab honest about WHO the session is.
 *
 * The access token is httpOnly, so the browser learns its identity only from
 * `/users/me`. That answer is cached, and the identity behind the cookie can
 * change underneath it without this tab doing anything:
 *
 *   - an impersonation session ends (stopped, expired, superseded), and the next
 *     refresh, made with the admin's refresh cookie, makes every later request
 *     run as the ADMIN;
 *   - another tab starts or stops impersonating, or another person signs in on
 *     a shared PC, and the cookie jar is shared.
 *
 * In both cases the cached `/users/me` (and every other cached response) still
 * describes the previous identity, so the banner lies. The fix is one reset
 * path: forget the saved branch, drop the whole query cache, reload.
 *
 * WHO RESETS. A tab resets only when its OWN fresh `/users/me` differs from the
 * identity it rendered with (its baseline), or when it caused the change itself
 * (start, stop). Such a tab also broadcasts. A tab that receives the broadcast
 * does not reset on the message alone: it re-checks `/users/me`, resets only if
 * its identity really changed (or if it cannot get an answer at all, see
 * createIdentityTracker), and never re-broadcasts. So a tab that is already
 * correct keeps its unsaved form state (review #3 L1), and one real change
 * produces one broadcast.
 *
 * The pure pieces (deadline maths, mismatch detection, message filtering, the
 * reset/broadcast decision) are exported separately so they can be tested
 * without a browser.
 */

import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { CurrentUserProfileResponse } from "@/types/user";

export const IDENTITY_CHANNEL_NAME = "novaerp-identity";

/**
 * Same key as UserBranchContext's BRANCH_STORAGE_KEY (and the literal in
 * nextjs-fetch.ts). Duplicated rather than imported so this module does not
 * pull a React context into non-React code; identity-sync.test.ts pins the two
 * together.
 */
export const BRANCH_STORAGE_KEY = "novaerp_current_branch_id";

// ─── Identity comparison ─────────────────────────────────────────────────────

export interface ShownIdentity {
  id: number;
  impersonatedBy: number | null;
}

export function identityOf(
  user: Pick<CurrentUserProfileResponse, "id" | "impersonated_by"> | null | undefined,
): ShownIdentity | null {
  if (!user) return null;
  return { id: user.id, impersonatedBy: user.impersonated_by ?? null };
}

/**
 * True when `fresh` is a different session identity from the one this tab is
 * rendering. Unknown on either side is NOT a change: a cleared cache or a
 * failed fetch says nothing about who the cookie belongs to.
 */
export function identityChanged(
  shown: ShownIdentity | null,
  fresh: ShownIdentity | null,
): boolean {
  if (!shown || !fresh) return false;
  return shown.id !== fresh.id || shown.impersonatedBy !== fresh.impersonatedBy;
}

/**
 * Refetch `/users/me` and return the identity from a FRESH answer, or null when
 * there is none.
 *
 * React Query's refetchQueries swallows errors: a failed refetch resolves
 * normally, and the cache still holds the previous identity (with the query in
 * an error state). Reading the cache after a failure therefore "confirms" the
 * stale identity (review #4 L2). So freshness is decided by `dataUpdateCount`,
 * which React Query increments on every successful fetch: if it has not moved,
 * nothing new arrived. (Not `dataUpdatedAt`: a refetch that lands in the same
 * millisecond would look like no answer.)
 *
 * Cancels any in-flight fetch first (it may have left before another tab
 * changed the cookie). If that fetch was itself superseded, for example by the
 * re-check after a token refresh, it joins the replacement instead of judging
 * the old answer.
 */
export async function recheckFreshIdentity(
  queryClient: Pick<QueryClient, "getQueryState" | "refetchQueries" | "isFetching">,
  queryKey: QueryKey,
): Promise<ShownIdentity | null> {
  const before = queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0;
  await queryClient.refetchQueries({ queryKey, exact: true }, { cancelRefetch: true });
  if (queryClient.isFetching({ queryKey, exact: true }) > 0) {
    await queryClient.refetchQueries({ queryKey, exact: true }, { cancelRefetch: false });
  }
  const state = queryClient.getQueryState<CurrentUserProfileResponse>(queryKey);
  if (!state || state.dataUpdateCount <= before) return null;
  return identityOf(state.data);
}

/**
 * How long a tab keeps trying when a broadcast re-check gets no fresh answer,
 * before it gives up and resets. Each attempt also carries React Query's own
 * retries, so the whole window is roughly half a minute.
 */
export const BROADCAST_RECHECK_RETRY_DELAYS_MS: readonly number[] = [1000, 3000, 7000];

export interface IdentityTrackerOptions {
  retryDelaysMs?: readonly number[];
  /** Injected for tests; defaults to a real timer. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Decides, for one page, when a `/users/me` answer means "reset" and whether
 * that reset should tell the other tabs.
 *
 * - `observe` is fed every answer the page receives through its own fetching
 *   (initial load, focus re-check, the re-check after a token refresh). The
 *   first answer becomes the baseline. A later mismatch resets WITH a
 *   broadcast, because this tab discovered the change itself.
 * - `onBroadcast` is called when another tab reports a change. It re-checks,
 *   and resets only on a real mismatch, WITHOUT a broadcast, because the sender
 *   has already told every tab.
 *
 * NO ANSWER FAILS CLOSED. If the re-check cannot get a fresh answer (network
 * down, backend unreachable), it tries again after each of retryDelaysMs. If
 * there is still nothing, and nothing fresher reached `observe` meanwhile, the
 * tab resets anyway, without broadcasting. It has been told the identity
 * changed and cannot confirm otherwise, and a needless reload is cheaper than a
 * tab that shows one identity while running as another. A page that has not
 * shown an identity yet has nothing to be wrong about, so it just waits for
 * its first answer.
 *
 * WHY THIS CANNOT STORM:
 * 1. A message is posted only by a page that is resetting because of its own
 *    discovery or its own start/stop, and a page resets at most once (the
 *    page-wide guard in createIdentitySync).
 * 2. Receiving a message never posts one: onBroadcast only ever resets with
 *    `broadcast: false`, including the fail-closed reset, and while its
 *    re-check (with retries) is out, `observe` defers to it.
 * 3. A page that reloads takes the cookie's current identity as its baseline,
 *    so without another real change it has nothing to find.
 * So one real change yields one broadcast in the normal case. Even in the
 * worst interleaving (a tab's own detection lands after its broadcast re-check
 * has settled), broadcasts are bounded by the number of tabs that were actually
 * stale, never by the number of messages received.
 */
export interface IdentityTracker {
  /** The identity this page rendered with, once known. */
  shown(): ShownIdentity | null;
  observe(fresh: ShownIdentity | null): void;
  /**
   * `recheck` must fetch `/users/me` afresh and resolve with the identity from
   * a FRESH answer, or null when no fresh answer arrived (recheckFreshIdentity).
   */
  onBroadcast(recheck: () => Promise<ShownIdentity | null>): Promise<void>;
}

const realSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function createIdentityTracker(
  onMismatch: (options: { broadcast: boolean }) => void,
  options: IdentityTrackerOptions = {},
): IdentityTracker {
  const retryDelaysMs = options.retryDelaysMs ?? BROADCAST_RECHECK_RETRY_DELAYS_MS;
  const sleep = options.sleep ?? realSleep;

  let shown: ShownIdentity | null = null;
  let latest: ShownIdentity | null = null;
  let observedCount = 0;
  let pendingBroadcastChecks = 0;

  const judge = (fresh: ShownIdentity | null, broadcast: boolean) => {
    if (!fresh) return;
    if (shown === null) {
      shown = fresh;
      return;
    }
    if (identityChanged(shown, fresh)) onMismatch({ broadcast });
  };

  const attempt = async (recheck: () => Promise<ShownIdentity | null>) => {
    try {
      return await recheck();
    } catch {
      return null;
    }
  };

  return {
    shown: () => shown,

    observe(fresh) {
      if (fresh) {
        latest = fresh;
        observedCount++;
      }
      // A broadcast-triggered re-check is out, and its answer arrives here too.
      // Leave the verdict to onBroadcast, which will not re-broadcast. Judging
      // here would turn a received message into a sent one.
      if (pendingBroadcastChecks > 0) return;
      judge(fresh, true);
    },

    async onBroadcast(recheck) {
      pendingBroadcastChecks++;
      const observedBefore = observedCount;
      let fresh: ShownIdentity | null = null;
      try {
        fresh = await attempt(recheck);
        for (const delay of retryDelaysMs) {
          if (fresh) break;
          await sleep(delay);
          fresh = await attempt(recheck);
        }
      } finally {
        pendingBroadcastChecks--;
      }

      if (fresh) {
        judge(fresh, false);
        return;
      }
      // The re-checks got nothing, but an answer that reached observe while
      // they were out is just as fresh.
      if (observedCount > observedBefore) {
        judge(latest, false);
        return;
      }
      if (shown === null) return;
      // Fail closed: told the identity changed, unable to confirm otherwise.
      onMismatch({ broadcast: false });
    },
  };
}

// ─── Impersonation countdown ─────────────────────────────────────────────────

/**
 * When the impersonation session ends, in THIS browser's clock.
 *
 * The server sends seconds remaining, measured when it answered, rather than an
 * absolute expiry: clinic PCs drift by minutes, and an absolute timestamp
 * compared against a drifted clock would end the session early or show time
 * that no longer exists. `receivedAt` is the query's `dataUpdatedAt`, which is
 * this browser's own clock, so the drift cancels out.
 *
 * Returns null when there is nothing to count down (not impersonating, an older
 * backend that omits the field, or no data yet).
 */
export function impersonationDeadline(
  receivedAt: number,
  expiresInSeconds: number | null | undefined,
): number | null {
  if (expiresInSeconds === null || expiresInSeconds === undefined) return null;
  if (!Number.isFinite(expiresInSeconds) || !(receivedAt > 0)) return null;
  return receivedAt + Math.max(0, expiresInSeconds) * 1000;
}

/** Whole seconds left, rounded up so the display reaches 0:00 only at the end. */
export function secondsRemaining(deadline: number, now: number): number {
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

/** `m:ss`, e.g. 14:05, 0:09. */
export function formatRemaining(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// ─── Cross-tab reset ─────────────────────────────────────────────────────────

export type IdentityChangeReason =
  | "impersonation-started"
  | "impersonation-stopped"
  | "identity-changed"
  | "session-ended";

export interface IdentityMessage {
  type: "identity-changed";
  sender: string;
  reason: IdentityChangeReason;
}

/**
 * Accepts only well-formed identity messages from ANOTHER sender. A
 * BroadcastChannel never delivers a message back to the instance that posted
 * it, but a second instance in the same tab would receive it, so the sender
 * check is what makes "a tab never reacts to its own broadcast" hold.
 */
export function isForeignIdentityMessage(
  data: unknown,
  selfId: string,
): data is IdentityMessage {
  if (!data || typeof data !== "object") return false;
  const msg = data as Partial<IdentityMessage>;
  return (
    msg.type === "identity-changed" &&
    typeof msg.sender === "string" &&
    msg.sender !== selfId
  );
}

interface ChannelLike {
  postMessage(message: unknown): void;
  addEventListener(type: "message", listener: (event: MessageEvent) => void): void;
  removeEventListener(type: "message", listener: (event: MessageEvent) => void): void;
}

export interface IdentitySyncDeps {
  tabId: string;
  /** Null where BroadcastChannel does not exist. */
  createChannel: () => ChannelLike | null;
  storage: () => Pick<Storage, "removeItem"> | null;
  /** A full document navigation: to `to`, or a reload of the current URL. */
  navigate: (to: string | null) => void;
}

export interface ResetOptions {
  reason: IdentityChangeReason;
  /**
   * Tell the other tabs to re-check. True only when THIS tab discovered or
   * caused the change (start, stop, its own mismatch). False for a reset that
   * followed a received message: the sender has already told everyone.
   */
  broadcast: boolean;
  /** Where to land; omitted means reload the current URL. */
  navigateTo?: string;
}

export interface IdentitySync {
  /** Returns false when a reset is already under way in this page. */
  reset(queryClient: Pick<QueryClient, "clear">, options: ResetOptions): boolean;
  /**
   * Subscribe to other tabs' messages. The handler decides what to do (see
   * IdentityTracker.onBroadcast); receiving never resets by itself. Messages
   * that arrive while this page is already resetting are dropped. Returns the
   * unsubscribe.
   */
  listen(onMessage: (message: IdentityMessage) => void): () => void;
  isResetting(): boolean;
}

export function createIdentitySync(deps: IdentitySyncDeps): IdentitySync {
  // Once per page load. The expiry timer, a rejected request and a broadcast
  // re-check can all arrive within the same second; only the first one acts.
  // The page is about to be replaced, which is what resets this.
  let resetting = false;
  let channel: ChannelLike | null | undefined;

  const getChannel = (): ChannelLike | null => {
    if (channel === undefined) {
      try {
        channel = deps.createChannel();
      } catch {
        channel = null;
      }
    }
    return channel;
  };

  const reset: IdentitySync["reset"] = (queryClient, options) => {
    if (resetting) return false;
    resetting = true;

    if (options.broadcast) {
      const message: IdentityMessage = {
        type: "identity-changed",
        sender: deps.tabId,
        reason: options.reason,
      };
      try {
        // Posted before navigating: postMessage queues the message to the
        // other tabs synchronously, so the unload that follows cannot lose it.
        getChannel()?.postMessage(message);
      } catch {
        // A tab that cannot broadcast still resets itself; the others fall
        // back to their focus / 401 checks.
      }
    }

    // The previous identity's saved branch. Left in place, a branch-scoped
    // target inherits the admin's X-Branch-ID and gets 403 on every request.
    try {
      deps.storage()?.removeItem(BRANCH_STORAGE_KEY);
    } catch {
      // Storage can throw (private mode, blocked site data); the reload still
      // happens, which is the part that matters.
    }

    queryClient.clear();
    deps.navigate(options.navigateTo ?? null);
    return true;
  };

  const listen: IdentitySync["listen"] = (onMessage) => {
    const ch = getChannel();
    if (!ch) return () => {};
    const handler = (event: MessageEvent) => {
      if (resetting) return;
      if (!isForeignIdentityMessage(event.data, deps.tabId)) return;
      onMessage(event.data);
    };
    ch.addEventListener("message", handler);
    return () => ch.removeEventListener("message", handler);
  };

  return { reset, listen, isResetting: () => resetting };
}

function randomTabId(): string {
  // crypto.randomUUID needs a secure context, which a LAN http:// dev host is not.
  const c = typeof crypto !== "undefined" ? crypto : undefined;
  if (c && typeof c.randomUUID === "function") {
    try {
      return c.randomUUID();
    } catch {
      // fall through
    }
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

let browserIdentitySync: IdentitySync | null = null;

/** The page-wide instance. Browser only. */
export function identitySync(): IdentitySync {
  if (!browserIdentitySync) {
    browserIdentitySync = createIdentitySync({
      tabId: randomTabId(),
      createChannel: () =>
        typeof BroadcastChannel === "undefined"
          ? null
          : new BroadcastChannel(IDENTITY_CHANNEL_NAME),
      storage: () => (typeof window === "undefined" ? null : window.localStorage),
      navigate: (to) => {
        if (to) window.location.assign(to);
        else window.location.reload();
      },
    });
  }
  return browserIdentitySync;
}
